import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, CheckCircle2 } from "lucide-react";

// Always-visible "Install app" button. Installs directly when the browser
// offers it; otherwise sends the user to /app for step-by-step instructions.
export default function InstallButton({ variant = "public" }) {
    const [prompt, setPrompt] = useState(null);
    const [installed, setInstalled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const onPrompt = (e) => {
            e.preventDefault();
            setPrompt(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        if (window.matchMedia?.("(display-mode: standalone)").matches) setInstalled(true);
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const handleClick = async () => {
        if (prompt) {
            prompt.prompt();
            try {
                await prompt.userChoice;
            } catch {
                /* user dismissed */
            }
            setPrompt(null);
        } else {
            navigate("/app");
        }
    };

    const label = installed ? "App installed" : "Install app";
    const Icon = installed ? CheckCircle2 : Download;

    if (variant === "admin") {
        return (
            <button
                data-testid="install-app-admin"
                onClick={handleClick}
                disabled={installed}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-sky-300/80 hover:bg-blue-500/10 hover:text-sky-200 disabled:opacity-60"
            >
                <Icon className="h-4 w-4" /> {label}
            </button>
        );
    }

    return (
        <button
            data-testid="install-app-public"
            onClick={handleClick}
            disabled={installed}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 px-4 py-2 text-sm text-amber-100 transition-colors hover:bg-amber-500/10 disabled:opacity-60"
        >
            <Icon className="h-3.5 w-3.5" /> {installed ? "Installed" : "Install app"}
        </button>
    );
}
